import React, { useState } from 'react';

/**
 * PlayerAvatar - Thumbnail photo component for registration workflows
 * Shows photo if available, otherwise shows initials with colored background
 */
export const PlayerAvatar = ({ 
  photoUrl, 
  firstName, 
  lastName, 
  size = 'md', // xs, sm, md, lg, xl
  className = '',
  onClick
}) => {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    xs: 'w-8 h-8 text-xs',
    sm: 'w-10 h-10 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl'
  };

  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  
  // Generate consistent color based on name
  const getColorFromName = (name) => {
    const colors = [
      'from-navy to-light-blue',
      'from-blue-500 to-blue-600',
      'from-emerald-500 to-emerald-600',
      'from-amber-500 to-amber-600',
      'from-rose-500 to-rose-600',
      'from-purple-500 to-purple-600',
      'from-cyan-500 to-cyan-600',
      'from-indigo-500 to-indigo-600'
    ];
    const nameStr = `${firstName || ''}${lastName || ''}`;
    const hash = nameStr.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const showInitials = !photoUrl || imageError;
  
  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-full overflow-hidden flex-shrink-0
        ${showInitials ? `bg-gradient-to-br ${getColorFromName(firstName + lastName)}` : ''}
        flex items-center justify-center
        ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {showInitials ? (
        <span className="text-white font-semibold">{initials}</span>
      ) : (
        <img
          src={photoUrl}
          alt={`${firstName} ${lastName}`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};

/**
 * UserAvatar - For parent/guardian profiles in registration context
 */
export const UserAvatar = ({ 
  photoUrl, 
  name, 
  size = 'md',
  className = '',
  onClick
}) => {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    xs: 'w-8 h-8 text-xs',
    sm: 'w-10 h-10 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl'
  };

  const nameParts = (name || '').split(' ');
  const initials = nameParts.length >= 2 
    ? `${nameParts[0]?.[0] || ''}${nameParts[nameParts.length - 1]?.[0] || ''}`.toUpperCase()
    : (name?.[0] || '?').toUpperCase();
  
  // Generate consistent color based on name
  const getColorFromName = (str) => {
    const colors = [
      'from-navy to-light-blue',
      'from-blue-500 to-blue-600',
      'from-emerald-500 to-emerald-600',
      'from-amber-500 to-amber-600',
      'from-rose-500 to-rose-600',
      'from-purple-500 to-purple-600',
      'from-cyan-500 to-cyan-600',
      'from-indigo-500 to-indigo-600'
    ];
    const hash = (str || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const showInitials = !photoUrl || imageError;
  
  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-full overflow-hidden flex-shrink-0
        ${showInitials ? `bg-gradient-to-br ${getColorFromName(name)}` : ''}
        flex items-center justify-center
        ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {showInitials ? (
        <span className="text-white font-semibold">{initials}</span>
      ) : (
        <img
          src={photoUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};

/**
 * PhotoUpload - Simple photo upload component for registration forms
 */
export const PhotoUpload = ({
  currentPhotoUrl,
  onPhotoChange,
  disabled = false,
  size = 'lg',
  firstName = '',
  lastName = ''
}) => {
  const [uploading, setUploading] = useState(false);
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }
    
    setUploading(true);
    
    try {
      // Convert to base64 data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result;
        onPhotoChange(dataUrl);
        setUploading(false);
      };
      reader.onerror = () => {
        alert('Failed to read image');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert('Failed to upload image');
      setUploading(false);
    }
  };
  
  const handleRemove = () => {
    onPhotoChange(null);
  };
  
  return (
    <div className="flex items-center space-x-4">
      <PlayerAvatar
        photoUrl={currentPhotoUrl}
        firstName={firstName}
        lastName={lastName}
        size={size}
      />
      <div className="flex flex-col space-y-2">
        <label className={`
          px-3 py-1.5 text-sm font-medium rounded-md border
          ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-navy border-navy hover:bg-navy/5 cursor-pointer'}
          transition-colors
        `}>
          {uploading ? 'Uploading...' : currentPhotoUrl ? 'Change Photo' : 'Add Photo'}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled || uploading}
            className="hidden"
          />
        </label>
        {currentPhotoUrl && !disabled && (
          <button
            type="button"
            onClick={handleRemove}
            className="text-sm text-red hover:underline text-left"
          >
            Remove
          </button>
        )}
        <p className="text-xs text-gray-500">Max 2MB, JPG or PNG</p>
      </div>
    </div>
  );
};

export default PlayerAvatar;
